import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Share2,
  Eye,
  Pencil,
  Clock,
  Plus,
  Search,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Link2,
  Wifi,
  WifiOff,
  Globe,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { ConfirmDialog, PromptDialog } from '@/components/ui/Modal';
import { PageLayout } from '@/components/layout/PageLayout';
import { EmbeddedSkillRail } from '@/components/skill';
import {
  tabService,
  type Collaborator,
  type SharedSession,
  type CollaborationActivityRow,
  type WorkspaceMember,
} from '@/services/tabs';
import { wsService } from '@/services/websocket.service';
import { useUser } from '@/stores/authStore';
import { toast } from 'sonner';
import { InviteMemberModal } from '@/components/collaboration/InviteMemberModal';
import { CreateSharedSessionModal } from '@/components/collaboration/CreateSharedSessionModal';
import { IncomingInvitesBanner } from '@/components/collaboration/IncomingInvitesBanner';
import { PendingInvitesCard } from '@/components/collaboration/PendingInvitesCard';
import { TabsInWorkspaceCard } from '@/components/collaboration/TabsInWorkspaceCard';
import { MemberRowMenu } from '@/components/collaboration/MemberRowMenu';
import { RoleBadge } from '@/components/collaboration/RoleBadge';

type PromptAction = { kind: 'renameSession'; workspaceId: string; defaultValue: string };

// (UUID validation is performed server-side; the legacy "add member by UUID"
// prompt has been removed in favour of the email-based invite flow.)
function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    const ch = [...parts[0].toUpperCase()];
    return ch.slice(0, 2).join('') || '?';
  }
  const a = [...parts[0].toUpperCase()][0] ?? '';
  const b = [...parts[parts.length - 1].toUpperCase()][0] ?? '';
  return (a + b) || '?';
}

function formatRelativeTime(iso: string): string {
  if (!iso) return '';
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const seconds = Math.floor((Date.now() - ts) / 1000);
  if (seconds < 0) return 'just now';
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function isAdminRole(role: string | undefined): boolean {
  if (!role) return false;
  const r = role.toLowerCase();
  return r === 'owner' || r === 'admin';
}

function isEditorOrAbove(role: string | undefined): boolean {
  if (!role) return false;
  const r = role.toLowerCase();
  return r === 'owner' || r === 'admin' || r === 'editor';
}

function statusLabel(status: Collaborator['status']): string {
  if (status === 'online') return 'Online';
  if (status === 'away') return 'Away';
  if (status === 'busy') return 'Busy';
  return 'Offline';
}

function sortCollaborators(list: Collaborator[]): Collaborator[] {
  const rank = (s: Collaborator['status']) => {
    if (s === 'online') return 0;
    if (s === 'away') return 1;
    if (s === 'busy') return 2;
    return 3;
  };
  return [...list].sort((a, b) => {
    if (a.name === 'You') return -1;
    if (b.name === 'You') return 1;
    const dr = rank(a.status) - rank(b.status);
    if (dr !== 0) return dr;
    return a.name.localeCompare(b.name);
  });
}

export function TabCollaborationHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useUser();
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [sessions, setSessions] = useState<SharedSession[]>([]);
  const [activities, setActivities] = useState<CollaborationActivityRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);
  const [expandedDetail, setExpandedDetail] = useState<{
    tab_ids: string[];
    members: Record<string, unknown>[];
    role?: string;
    is_owner?: boolean;
  } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  /** Cache of details keyed by workspace id to avoid flicker on quick re-toggles. */
  const detailCacheRef = useRef<Record<string, { tab_ids: string[]; members: Record<string, unknown>[]; role?: string; is_owner?: boolean }>>({});
  const [promptAction, setPromptAction] = useState<PromptAction | null>(null);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [inviteTarget, setInviteTarget] = useState<SharedSession | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'owned' | 'shared' | 'active'>('all');
  const [showActivity, setShowActivity] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [wsConnected, setWsConnected] = useState<boolean>(() => wsService.isConnected());
  const [wsConnectionState, setWsConnectionState] = useState<'disconnected' | 'connecting' | 'connected'>(
    () => wsService.getConnectionState()
  );
  const [addTabsSessionId, setAddTabsSessionId] = useState<string | null>(null);
  const [addTabsSignal, setAddTabsSignal] = useState(0);
  const hasLoadedRef = useRef(false);
  const sessionsRef = useRef(sessions);
  const expandedSessionIdRef = useRef(expandedSessionId);
  sessionsRef.current = sessions;
  expandedSessionIdRef.current = expandedSessionId;

  const fetchData = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    if (!silent) {
      setIsLoading(true);
      setLoadError(null);
    }
    try {
      const [collabData, workspaceData, activityData] = await Promise.all([
        tabService.getCollaborators(),
        tabService.getSharedWorkspaces(),
        tabService.getCollaborationHubActivity().catch(() => [] as CollaborationActivityRow[]),
      ]);
      setCollaborators(collabData);
      setSessions(workspaceData);
      setActivities(activityData);
      hasLoadedRef.current = true;
    } catch (error) {
      console.error('Failed to fetch collaboration data:', error);
      const msg = (error as Error)?.message ?? 'Failed to load collaboration data';
      if (!silent) {
        setLoadError(msg);
        toast.error(msg);
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, []);

  const refreshCollaborators = useCallback(async () => {
    try {
      const collabData = await tabService.getCollaborators();
      setCollaborators(collabData);
    } catch {
      /* presence refresh is best-effort */
    }
  }, []);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  // Accept invitation deep links from email: ?accept=<invitation-id>
  useEffect(() => {
    const token = searchParams.get('accept');
    if (!token) return;
    let cancelled = false;
    const accept = async () => {
      try {
        const result = await tabService.acceptWorkspaceInvitation(token);
        if (cancelled) return;
        toast.success('Invitation accepted — welcome to the shared session');
        const next = new URLSearchParams(searchParams);
        next.delete('accept');
        setSearchParams(next, { replace: true });
        if (result.workspace_id) {
          setExpandedSessionId(result.workspace_id);
        }
        void fetchData();
        setRefreshKey((k) => k + 1);
      } catch (err) {
        if (!cancelled) {
          toast.error((err as Error).message ?? 'Failed to accept invitation');
        }
      }
    };
    void accept();
    return () => {
      cancelled = true;
    };
  }, [searchParams, setSearchParams, fetchData]);

  const loadSessionDetail = useCallback(
    async (sessionId: string) => {
      if (expandedSessionId === sessionId && expandedDetail) {
        setExpandedSessionId(null);
        setExpandedDetail(null);
        return;
      }
      setDetailLoading(true);
      setExpandedSessionId(sessionId);
      try {
        const d = await tabService.getSharedWorkspaceDetail(sessionId);
        const payload = { tab_ids: d.tab_ids, members: d.members, role: d.role, is_owner: d.is_owner };
        setExpandedDetail(payload);
        detailCacheRef.current[sessionId] = payload;
      } catch (err) {
        toast.error((err as Error).message ?? 'Failed to load session details');
        setExpandedSessionId(null);
        setExpandedDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [expandedSessionId, expandedDetail]
  );

  const handleAddTabsToSession = useCallback(async (sessionId: string) => {
    if (expandedSessionId !== sessionId || !expandedDetail) {
      setDetailLoading(true);
      setExpandedSessionId(sessionId);
      try {
        const d = await tabService.getSharedWorkspaceDetail(sessionId);
        const payload = { tab_ids: d.tab_ids, members: d.members, role: d.role, is_owner: d.is_owner };
        setExpandedDetail(payload);
        detailCacheRef.current[sessionId] = payload;
      } catch (err) {
        toast.error((err as Error).message ?? 'Failed to load session details');
        return;
      } finally {
        setDetailLoading(false);
      }
    }
    setAddTabsSessionId(sessionId);
    setAddTabsSignal((n) => n + 1);
  }, [expandedSessionId, expandedDetail]);

  const handleRetryWebSocket = useCallback(() => {
    setWsConnectionState('connecting');
    void wsService.connect().catch(() => {
      setWsConnectionState(wsService.getConnectionState());
      setWsConnected(wsService.isConnected());
    });
  }, []);

  const handleShareSession = () => {
    setShowCreateSession(true);
  };

  const handleEditSession = (workspaceId: string, currentName: string) => {
    setPromptAction({ kind: 'renameSession', workspaceId, defaultValue: currentName });
  };

  const handlePromptSubmit = async (value: string) => {
    if (!promptAction) return;
    const action = promptAction;
    setPromptAction(null);

    switch (action.kind) {
      case 'renameSession': {
        const trimmed = value.trim();
        if (!trimmed) {
          toast.error('Session name cannot be empty');
          return;
        }
        try {
          await tabService.updateSharedWorkspace(action.workspaceId, { name: trimmed });
          toast.success('Session updated');
          void fetchData();
        } catch (err) {
          toast.error((err as Error).message ?? 'Failed to update session');
        }
        break;
      }
    }
  };

  const promptConfig = (() => {
    switch (promptAction?.kind) {
      case 'renameSession':
        return {
          title: 'Rename session',
          label: 'Session name',
          defaultValue: promptAction.defaultValue,
          submitText: 'Save',
        };
      default:
        return null;
    }
  })();

  const handleCopyShare = async (workspaceId: string) => {
    const inviteLink = `${window.location.origin}/tabs?section=collaboration&join=${workspaceId}`;
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success('Invite link copied to clipboard');
    } catch {
      // Fallback for non-secure contexts
      try {
        await navigator.clipboard.writeText(workspaceId);
        toast.success('Workspace ID copied to clipboard');
      } catch {
        toast.error('Could not copy to clipboard');
      }
    }
  };

  const handleDeleteSession = (workspaceId: string, name: string) => {
    setDeleteTarget({ id: workspaceId, name });
  };

  const confirmDeleteSession = async () => {
    if (!deleteTarget) return;
    try {
      await tabService.deleteSharedWorkspace(deleteTarget.id);
      toast.success('Shared session deleted');
      setDeleteTarget(null);
      if (expandedSessionId === deleteTarget.id) {
        setExpandedSessionId(null);
        setExpandedDetail(null);
      }
      void fetchData();
    } catch (err) {
      toast.error((err as Error).message ?? 'Failed to delete shared session');
    }
  };

  // Subscribe to real-time workspace events. Background refreshes are silent so
  // the UI does not flash loading spinners on every heartbeat or WS event.
  useEffect(() => {
    const silentFetch = () => void fetchData({ silent: true });
    const refreshDetail = async (wsId: string) => {
      if (expandedSessionIdRef.current === wsId) {
        try {
          const d = await tabService.getSharedWorkspaceDetail(wsId);
          const payload = { tab_ids: d.tab_ids, members: d.members, role: d.role, is_owner: d.is_owner };
          setExpandedDetail(payload);
          detailCacheRef.current[wsId] = payload;
        } catch {
          /* ignore */
        }
      }
    };
    const prependActivity = (text: string) => {
      setActivities((prev) => [
        { user: 'System', action: text, target: '', time: new Date().toISOString(), timeRel: 'just now' },
        ...prev,
      ]);
    };
    const isMyWs = (payload: unknown): boolean => {
      if (!payload || typeof payload !== 'object') return false;
      const id = (payload as Record<string, unknown>).workspace_id;
      return typeof id === 'string' && sessionsRef.current.some((s) => s.id === id);
    };
    const handlers: Array<[string, (data: unknown) => void]> = [
      ['user_joined', silentFetch],
      ['user_left', silentFetch],
      ['workspace_created', silentFetch],
      ['workspace_updated', silentFetch],
      ['workspace_deleted', (d) => {
        const id = (d as { workspace_id?: string } | undefined)?.workspace_id;
        if (id && expandedSessionIdRef.current === id) {
          setExpandedSessionId(null);
          setExpandedDetail(null);
        }
        silentFetch();
      }],
      ['workspace_member_added', (d) => {
        if (isMyWs(d)) {
          const x = d as { display?: string; email?: string } | undefined;
          prependActivity(`added ${x?.display ?? x?.email ?? 'a member'} to`);
        }
        silentFetch();
        const id = (d as { workspace_id?: string } | undefined)?.workspace_id;
        if (id) void refreshDetail(id);
      }],
      ['workspace_member_removed', (d) => {
        if (isMyWs(d)) prependActivity('removed a member from');
        silentFetch();
        const id = (d as { workspace_id?: string } | undefined)?.workspace_id;
        if (id) void refreshDetail(id);
      }],
      ['workspace_member_role_changed', (d) => {
        if (isMyWs(d)) prependActivity('changed a role in');
        silentFetch();
        const id = (d as { workspace_id?: string } | undefined)?.workspace_id;
        if (id) void refreshDetail(id);
      }],
      ['workspace_ownership_transferred', () => {
        silentFetch();
        if (expandedSessionIdRef.current) void refreshDetail(expandedSessionIdRef.current);
      }],
      ['workspace_tab_assigned', (d) => {
        if (isMyWs(d)) prependActivity('shared a tab in');
        silentFetch();
        const id = (d as { workspace_id?: string } | undefined)?.workspace_id;
        if (id) void refreshDetail(id);
      }],
      ['workspace_tab_unassigned', (d) => {
        if (isMyWs(d)) prependActivity('removed a tab from');
        silentFetch();
        const id = (d as { workspace_id?: string } | undefined)?.workspace_id;
        if (id) void refreshDetail(id);
      }],
      ['workspace_invitation_created', (d) => {
        const x = d as { email?: string } | undefined;
        if (x?.email) toast.success(`Invitation sent to ${x.email}`);
        if (isMyWs(d)) prependActivity(`invited ${x?.email ?? 'a user'} to`);
        setRefreshKey((k) => k + 1);
      }],
    ];
    handlers.forEach(([evt, fn]) => wsService.on(evt, fn));
    return () => {
      handlers.forEach(([evt, fn]) => wsService.off(evt, fn));
    };
  }, [fetchData]);

  // Connect WebSocket for live collaboration updates (page previously only polled isConnected).
  useEffect(() => {
    const onStatus = (data: unknown) => {
      const payload = data as { connected?: boolean } | undefined;
      const connected = payload?.connected === true || wsService.isConnected();
      setWsConnected(connected);
      setWsConnectionState(connected ? 'connected' : wsService.getConnectionState());
    };
    wsService.on('connection_status', onStatus);
    void wsService.connect().catch(() => {
      setWsConnectionState(wsService.getConnectionState());
      setWsConnected(wsService.isConnected());
    });
    setWsConnected(wsService.isConnected());
    setWsConnectionState(wsService.getConnectionState());
    return () => {
      wsService.off('connection_status', onStatus);
    };
  }, []);

  // Track WebSocket connection state between events.
  useEffect(() => {
    const interval = window.setInterval(() => {
      setWsConnected(wsService.isConnected());
      setWsConnectionState(wsService.getConnectionState());
    }, 2000);
    return () => window.clearInterval(interval);
  }, []);

  const sessionIdsKey = useMemo(
    () => sessions.map((s) => s.id).sort().join(','),
    [sessions]
  );

  // Heartbeat: ping workspaces on an interval. Only refresh collaborator
  // presence — never re-fetch the full page (that caused flicker + a loop).
  useEffect(() => {
    if (!sessionIdsKey) return;
    const ids = sessionIdsKey.split(',');
    const ping = async () => {
      await Promise.allSettled(ids.map((id) => tabService.heartbeatWorkspace(id)));
      await refreshCollaborators();
    };
    void ping();
    const intervalId = window.setInterval(() => void ping(), 30_000);
    return () => window.clearInterval(intervalId);
  }, [sessionIdsKey, refreshCollaborators]);

  // Group + filter sessions
  const filteredSessions = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return sessions.filter((s) => {
      if (needle && !s.name.toLowerCase().includes(needle)) return false;
      if (filter === 'owned' && s.owner !== 'You') return false;
      if (filter === 'shared' && s.owner === 'You') return false;
      if (filter === 'active' && s.activity !== 'Active now') return false;
      return true;
    });
  }, [sessions, search, filter]);

  const getStatusColor = (status: Collaborator['status']) => {
    if (status === 'online') return 'bg-green-500';
    if (status === 'away') return 'bg-yellow-500';
    if (status === 'busy') return 'bg-red-500';
    return 'bg-khiimori-text-muted';
  };

  const sortedCollaborators = useMemo(() => sortCollaborators(collaborators), [collaborators]);
  const onlineCount = collaborators.filter((c) => c.status === 'online').length;
  const totalMembers = collaborators.length;
  const sharedTabs = sessions.reduce((sum, s) => sum + s.tabCount, 0);
  const activeSessions = sessions.filter((s) => s.activity === 'Active now').length;
  const teamPresenceLabel =
    totalMembers === 0
      ? 'Invite teammates to collaborate'
      : `${onlineCount} of ${totalMembers} online`;
  const liveUpdatesLabel = wsConnected ? 'On' : 'Off';
  const liveUpdatesHint = wsConnected
    ? 'Realtime sync active'
    : wsConnectionState === 'connecting'
      ? 'Connecting…'
      : 'Tap to connect';
  const currentUserId = currentUser?.id ?? '';
  const callerRole = expandedDetail?.role;
  const callerIsOwner = expandedDetail?.is_owner ?? false;
  const canManageExpanded = isEditorOrAbove(callerRole) || callerIsOwner;
  const showInitialLoader = isLoading && !hasLoadedRef.current;

  return (
    <PageLayout
      title="Tab Collaboration Hub"
      description="Share tab collections with your team and collaborate in real time"
      actions={
        <button
          type="button"
          onClick={handleShareSession}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors shadow-sm shadow-primary-500/25"
        >
          <Plus className="w-4 h-4" />
          Share Session
        </button>
      }
    >
      {loadError && (
        <div className="flex items-center justify-between gap-3 p-3 rounded-md border border-red-500/30 bg-red-500/10 text-sm text-red-700 dark:text-red-300 mb-4">
          <span className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {loadError}
          </span>
          <button
            type="button"
            onClick={() => void fetchData()}
            className="text-red-700 dark:text-red-300 hover:underline inline-flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </div>
      )}

      <IncomingInvitesBanner refreshKey={refreshKey} onAccepted={() => void fetchData()} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Card className="p-4 border-l-4 border-l-blue-500">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted">Sessions</p>
              <p className="text-2xl font-bold text-khiimori-text-primary mt-1">{sessions.length}</p>
              <p className="text-xs text-khiimori-text-muted mt-0.5">
                {activeSessions} active now
              </p>
            </div>
            <Share2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-green-500">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted">Team members</p>
              <p className="text-2xl font-bold text-khiimori-text-primary mt-1">{totalMembers}</p>
              <p className="text-xs text-khiimori-text-muted mt-0.5">{teamPresenceLabel}</p>
            </div>
            <Users className="w-5 h-5 text-green-500 flex-shrink-0" />
          </div>
        </Card>
        <Card className="p-4 border-l-4 border-l-purple-500">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted">Shared tabs</p>
              <p className="text-2xl font-bold text-khiimori-text-primary mt-1">{sharedTabs}</p>
              <p className="text-xs text-khiimori-text-muted mt-0.5">Across all sessions</p>
            </div>
            <Eye className="w-5 h-5 text-purple-500 flex-shrink-0" />
          </div>
        </Card>
        <Card
          clickable={!wsConnected}
          className={`p-4 border-l-4 border-l-primary-500 ${!wsConnected ? 'hover:bg-khiimori-bg-elevated/30' : ''}`}
          onClick={!wsConnected ? handleRetryWebSocket : undefined}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-khiimori-text-muted">Live updates</p>
              <p className="text-2xl font-bold text-khiimori-text-primary mt-1">{liveUpdatesLabel}</p>
              <p className="text-xs text-khiimori-text-muted mt-0.5 flex items-center gap-1">
                {wsConnected ? <Wifi className="w-3 h-3 text-green-500" /> : <WifiOff className="w-3 h-3" />}
                {liveUpdatesHint}
              </p>
            </div>
          </div>
        </Card>
      </div>

      <EmbeddedSkillRail
        cardClassName="mb-6"
        title="Available Skills"
        description="Run a skill against the current collaboration state."
        restrictCategories={['collaborate']}
      />

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-lg font-semibold text-khiimori-text-primary">Shared Sessions</h3>
              <p className="text-sm text-khiimori-text-muted">Manage workspaces, members, and shared tabs</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-khiimori-text-muted" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter by name…"
                  className="pl-8 pr-3 py-1.5 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
                />
              </div>
              <div className="flex rounded-md border border-khiimori-border overflow-hidden">
                {(['all', 'owned', 'shared', 'active'] as const).map((f) => (
                  <button
                    key={f}
                    type="button"
                    onClick={() => setFilter(f)}
                    className={`px-2.5 py-1 text-xs ${
                      filter === f
                        ? 'bg-primary-500/20 text-khiimori-text-primary'
                        : 'text-khiimori-text-muted hover:bg-khiimori-bg-elevated/60'
                    }`}
                  >
                    {f === 'all' ? 'All' : f === 'owned' ? 'Owned' : f === 'shared' ? 'Shared' : 'Active'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {showInitialLoader ? (
            <Card className="p-6">
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
              </div>
            </Card>
          ) : filteredSessions.length === 0 ? (
            <Card className="p-6">
              <p className="text-sm text-khiimori-text-muted">
                {sessions.length === 0
                  ? 'No shared sessions yet. Use "Share Session" to create one.'
                  : 'No sessions match the current filter.'}
              </p>
            </Card>
          ) : (
            filteredSessions.map((session) => {
              const isOwner = session.owner === 'You';
              return (
                <div key={session.id}>
                  <Card hoverable className="p-5 overflow-hidden">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h4 className="text-lg font-semibold text-khiimori-text-primary">{session.name}</h4>
                          {isOwner && <RoleBadge role="owner" iconless />}
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              session.activity === 'Active now'
                                ? 'bg-green-500/15 text-green-400'
                                : 'bg-khiimori-bg-elevated text-khiimori-text-muted'
                            }`}
                          >
                            {session.activity}
                          </span>
                        </div>
                        <p className="text-sm text-khiimori-text-muted">
                          Owned by {session.owner}
                          {session.createdAt && (
                            <> · Created {new Date(session.createdAt).toLocaleDateString()}</>
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 mb-4">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-khiimori-bg-elevated/60 text-xs text-khiimori-text-secondary">
                        <Users className="w-3.5 h-3.5" />
                        {session.collaborators.length} member{session.collaborators.length === 1 ? '' : 's'}
                      </span>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-khiimori-bg-elevated/60 text-xs text-khiimori-text-secondary">
                        <Eye className="w-3.5 h-3.5" />
                        {session.tabCount} tab{session.tabCount === 1 ? '' : 's'}
                      </span>
                      <div className="flex -space-x-1.5 ml-auto">
                        {session.collaborators.slice(0, 5).map((name, i) => (
                          <div
                            key={i}
                            className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-500 to-khiimori-accent-fuchsia flex items-center justify-center text-white text-[10px] font-semibold border-2 border-khiimori-bg-secondary"
                            title={name}
                          >
                            {initialsFromName(name)}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 border-t border-khiimori-border/50 pt-4">
                      <button
                        type="button"
                        onClick={() => void loadSessionDetail(session.id)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        {expandedSessionId === session.id ? 'Hide details' : 'Open session'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setInviteTarget(session)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-khiimori-border text-sm text-khiimori-text-primary hover:bg-khiimori-bg-elevated/60 transition-colors"
                        title="Invite by email"
                      >
                        <Users className="w-4 h-4" /> Invite
                      </button>
                      {(isOwner || (expandedSessionId === session.id && canManageExpanded)) && (
                        <button
                          type="button"
                          onClick={() => void handleAddTabsToSession(session.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary-500/40 bg-primary-500/10 text-sm text-primary-300 hover:bg-primary-500/20 transition-colors shrink-0 whitespace-nowrap"
                          title="Share tabs from your Live session"
                        >
                          <Globe className="w-4 h-4" /> Add tabs
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => void handleCopyShare(session.id)}
                        className="p-2 rounded-lg border border-khiimori-border text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/60 transition-colors"
                        title="Copy invite link"
                      >
                        <Link2 className="w-4 h-4" />
                      </button>
                      {(isOwner || (expandedSessionId === session.id && (isAdminRole(callerRole) || callerIsOwner))) && (
                        <button
                          type="button"
                          onClick={() => handleEditSession(session.id, session.name)}
                          className="p-2 rounded-lg border border-khiimori-border text-khiimori-text-muted hover:text-khiimori-text-primary hover:bg-khiimori-bg-elevated/60 transition-colors"
                          title="Rename"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      <div className="flex-1" />
                      {isOwner ? (
                        <button
                          type="button"
                          onClick={() => handleDeleteSession(session.id, session.name)}
                          className="px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-500/10 transition-colors"
                          title="Delete session"
                        >
                          Delete
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setDeleteTarget({ id: session.id, name: session.name })}
                          className="px-3 py-2 rounded-lg text-sm text-red-500 hover:bg-red-500/10 transition-colors"
                          title="Leave session"
                        >
                          Leave
                        </button>
                      )}
                    </div>

                    <AnimatePresence>
                      {expandedSessionId === session.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600 overflow-hidden"
                        >
                          {detailLoading && !detailCacheRef.current[session.id] ? (
                            <p className="text-sm text-khiimori-text-muted">Loading…</p>
                          ) : (
                            <ExpandedSessionDetail
                              workspaceId={session.id}
                              detail={expandedDetail}
                              isAdmin={isAdminRole(callerRole) || callerIsOwner}
                              isEditor={canManageExpanded}
                              canEditTabs={canManageExpanded}
                              isOwner={callerIsOwner}
                              currentUserUuid={currentUserId}
                              refreshKey={refreshKey}
                              onChanged={() => {
                                void fetchData({ silent: true });
                                setRefreshKey((k) => k + 1);
                              }}
                              onInviteClick={() => setInviteTarget(session)}
                              openAddTabsSignal={
                                addTabsSessionId === session.id ? addTabsSignal : 0
                              }
                            />
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </div>
              );
            })
          )}
        </div>

        <div className="xl:col-span-4 space-y-4">
          <Card className="p-5 sticky top-4">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="text-base font-semibold text-khiimori-text-primary flex items-center gap-2">
                <Users className="w-4 h-4 text-primary-400" /> Team
              </h3>
              <span className="text-xs text-khiimori-text-muted">
                {onlineCount} of {totalMembers} online
              </span>
            </div>
            <p className="text-xs text-khiimori-text-muted mb-4">
              People across your shared sessions
            </p>
            {showInitialLoader ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin w-7 h-7 border-2 border-primary-500 border-t-transparent rounded-full" />
              </div>
            ) : sortedCollaborators.length === 0 ? (
              <div className="py-6 text-sm text-khiimori-text-muted text-center space-y-3">
                <p>No team members yet.</p>
                <button
                  type="button"
                  onClick={handleShareSession}
                  className="text-sm text-primary-400 hover:underline inline-flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Create a session
                </button>
              </div>
            ) : (
              <ul className="space-y-2">
                {sortedCollaborators.map((collab) => (
                  <li
                    key={collab.id}
                    className="flex items-center gap-3 p-2.5 rounded-lg border border-khiimori-border/40 bg-khiimori-bg-elevated/30"
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-500 to-khiimori-accent-fuchsia flex items-center justify-center text-white font-semibold text-xs">
                        {collab.avatar || initialsFromName(collab.name)}
                      </div>
                      <div
                        className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 ${getStatusColor(
                          collab.status
                        )} rounded-full border-2 border-khiimori-bg-secondary`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-khiimori-text-primary truncate">{collab.name}</p>
                      <p className="text-xs text-khiimori-text-muted truncate">
                        {collab.viewing ? `Viewing ${collab.viewing}` : statusLabel(collab.status)}
                      </p>
                    </div>
                    <span
                      className={`text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${
                        collab.status === 'online'
                          ? 'bg-green-500/15 text-green-400'
                          : 'bg-khiimori-bg-elevated text-khiimori-text-muted'
                      }`}
                    >
                      {statusLabel(collab.status)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card className="p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-khiimori-text-primary flex items-center gap-2">
                <Clock className="w-4 h-4 text-primary-400" /> Activity
              </h3>
              <button
                type="button"
                onClick={() => setShowActivity((v) => !v)}
                className="text-xs text-khiimori-text-muted hover:text-khiimori-text-primary"
              >
                {showActivity ? 'Hide' : 'Show'}
              </button>
            </div>
            {showActivity ? (
              activities.length === 0 ? (
                <p className="text-sm text-khiimori-text-muted">No recent activity yet.</p>
              ) : (
                <ul className="space-y-2 max-h-64 overflow-y-auto">
                  {activities.slice(0, 10).map((activity, idx) => (
                    <li
                      key={`${activity.user}-${activity.time}-${idx}`}
                      className="text-xs p-2 rounded-md bg-khiimori-bg-elevated/40 border border-khiimori-border/30"
                    >
                      <span className="font-medium text-khiimori-text-primary">{activity.user}</span>{' '}
                      <span className="text-khiimori-text-secondary">{activity.action}</span>
                      {activity.target && (
                        <span className="text-primary-400"> {activity.target}</span>
                      )}
                      <p className="text-khiimori-text-muted mt-0.5">
                        {activity.timeRel ?? formatRelativeTime(activity.time)}
                      </p>
                    </li>
                  ))}
                </ul>
              )
            ) : (
              <p className="text-xs text-khiimori-text-muted">Session changes and invites appear here.</p>
            )}
          </Card>
        </div>
      </div>

      <PromptDialog
        isOpen={promptAction !== null}
        onClose={() => setPromptAction(null)}
        onSubmit={(value) => void handlePromptSubmit(value)}
        title={promptConfig?.title ?? ''}
        label={promptConfig?.label}
        defaultValue={promptConfig?.defaultValue ?? ''}
        submitText={promptConfig?.submitText}
      />

      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget && sessions.find((s) => s.id === deleteTarget.id)?.owner !== 'You') {
            // Non-owner = leaving
            tabService
              .leaveWorkspace(deleteTarget.id)
              .then(() => {
                toast.success('Left workspace');
                setDeleteTarget(null);
                if (expandedSessionId === deleteTarget.id) {
                  setExpandedSessionId(null);
                  setExpandedDetail(null);
                }
                void fetchData();
              })
              .catch((err: Error) => toast.error(err.message ?? 'Failed to leave'));
          } else {
            void confirmDeleteSession();
          }
        }}
        title="Confirm action"
        message={
          deleteTarget
            ? sessions.find((s) => s.id === deleteTarget.id)?.owner === 'You'
              ? `Delete "${deleteTarget.name}"? Collaborators will lose access.`
              : `Leave "${deleteTarget.name}"? You will lose access to its shared tabs.`
            : ''
        }
        confirmText={deleteTarget && sessions.find((s) => s.id === deleteTarget.id)?.owner === 'You' ? 'Delete' : 'Leave'}
        variant="warning"
      />

      {inviteTarget && (
        <InviteMemberModal
          isOpen
          onClose={() => setInviteTarget(null)}
          workspaceId={inviteTarget.id}
          workspaceName={inviteTarget.name}
          onInvited={() => {
            setRefreshKey((k) => k + 1);
            void fetchData();
          }}
        />
      )}

      <CreateSharedSessionModal
        isOpen={showCreateSession}
        onClose={() => setShowCreateSession(false)}
        onCreated={() => {
          setRefreshKey((k) => k + 1);
          void fetchData();
        }}
      />
    </PageLayout>
  );
}

interface ExpandedSessionDetailProps {
  workspaceId: string;
  detail: { tab_ids: string[]; members: Record<string, unknown>[]; role?: string; is_owner?: boolean } | null;
  isAdmin: boolean;
  isEditor: boolean;
  canEditTabs: boolean;
  isOwner: boolean;
  currentUserUuid: string;
  refreshKey: number;
  onChanged: () => void;
  onInviteClick: () => void;
  openAddTabsSignal?: number;
}

const ExpandedSessionDetail: React.FC<ExpandedSessionDetailProps> = ({
  workspaceId,
  detail,
  isAdmin,
  isEditor: _isEditor,
  canEditTabs,
  isOwner,
  currentUserUuid,
  refreshKey,
  onChanged,
  onInviteClick,
  openAddTabsSignal = 0,
}) => {
  if (!detail) {
    return <p className="text-sm text-khiimori-text-muted">No details available.</p>;
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-4">
        <PendingInvitesCard
          workspaceId={workspaceId}
          isAdmin={isAdmin}
          refreshKey={refreshKey}
          onInviteClick={onInviteClick}
          onChange={onChanged}
        />

        <Card className="p-4">
          <p className="font-medium text-khiimori-text-primary mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary-400" />
            Members ({detail.members.length})
          </p>
        {detail.members.length === 0 ? (
          <p className="text-sm text-khiimori-text-muted">No members yet.</p>
        ) : (
          <ul className="space-y-1">
            {(detail.members as unknown as WorkspaceMember[]).map((m, mi) => {
              const userUuid = m.user_uuid ?? '';
              const name = m.display_name || m.email || `User ${m.user_id}`;
              const isSelf = userUuid && userUuid === currentUserUuid;
              return (
                <li
                  key={mi}
                  className="flex items-center gap-2 text-sm text-khiimori-text-primary py-1"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
                    {initialsFromName(name)}
                  </div>
                  <span className="flex-1 truncate">
                    {isSelf ? <span className="font-semibold">You</span> : name}
                    {isSelf && (
                      <span className="ml-1 text-xs text-khiimori-text-muted">(self)</span>
                    )}
                  </span>
                  <RoleBadge role={m.role} />
                  <MemberRowMenu
                    workspaceId={workspaceId}
                    member={m}
                    callerRole={detail.role ?? ''}
                    callerIsOwner={isOwner}
                    callerUserUuid={currentUserUuid}
                    onChanged={onChanged}
                  />
                </li>
              );
            })}
          </ul>
        )}
        {isAdmin && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-500" />
            <span className="text-xs text-khiimori-text-muted">
              You can invite, change roles, and remove members.
            </span>
          </div>
        )}
        </Card>
      </div>

      <TabsInWorkspaceCard
        workspaceId={workspaceId}
        canEdit={canEditTabs}
        refreshKey={refreshKey}
        openAddTabsSignal={openAddTabsSignal}
        onChange={onChanged}
      />
    </div>
  );
};

export default TabCollaborationHub;
